import React, { useEffect, useId, useState } from 'react'
import { DotLottieReact, type DotLottieReactProps, type DotLottie } from '@lottiefiles/dotlottie-react'
import { useIntersectionObserver } from 'usehooks-ts'
import { Asset } from 'contentful'

import { cx } from '@/utils/strings'
import type { TypeComponentLottie } from '@/types/ctf'
import usePrevious from '@/utils/usePrevious'

type LottieEntry = TypeComponentLottie<'WITHOUT_UNRESOLVABLE_LINKS'>

export type LottieProps = Partial<Omit<LottieEntry['fields'], 'autoplayType' | 'lottieFile'>> & {
  className?: string
  lottieFile?:
    | Asset
    | {
        fields?: {
          file: {
            url: string
          }
        }
        file?: {
          url: string
        }
      }
  animationData?: DotLottieReactProps['src']
  /**
   * Only show a specific frame. For reduced motion.
   */
  onlyShowFrame?: number | 'last'
  playerRef?: React.RefObject<DotLottie>
  marker?: string
  autoplayType?: LottieEntry['fields']['autoplayType'] | 'visible' | 'hover' | 'click' | 'loop' | 'none'
  onInit?: (ref: DotLottie | null) => void
  onReady?: (ref: DotLottie) => void
  onComplete?: (ref: DotLottie) => void
  onLoad?: (ref: DotLottie | null) => void
}

export default function Lottie({
  className,
  animationData,
  lottieFile,
  autoplayType: _autoplayType = 'visible',
  onlyShowFrame,
  marker,
  onInit,
  onReady,
  onComplete,
  onLoad,
}: LottieProps) {
  const id = useId()
  const lottieId = `lottie-${id.replace(/:/g, '')}`

  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const src = animationData || lottieFile?.fields?.file?.url || lottieFile?.file?.url
  const prevSrc = usePrevious(src)

  const { entry, ref: containerRef } = useIntersectionObserver({ threshold: 0.5, freezeOnceVisible: true })
  const isVisible = !!entry?.isIntersecting

  const autoplayType = _autoplayType.toLowerCase()
  const autoplay = autoplayType !== 'none' && (autoplayType === 'visible' || autoplayType === 'loop')
  const loop = autoplayType === 'loop'

  const play = () => {
    if (typeof onlyShowFrame === 'number') {
      dotLottie?.setSegment(onlyShowFrame, onlyShowFrame)
      dotLottie?.play()
    } else if (onlyShowFrame === 'last') {
      const duration = dotLottie?.totalFrames || 0
      dotLottie?.setFrame(duration)
    } else {
      dotLottie?.play()
    }
  }

  // Handle when autoplayType changes
  useEffect(() => {
    if (autoplayType === 'none') {
      dotLottie?.stop()
    } else if ((autoplayType === 'visible' && isVisible) || autoplayType === 'loop') {
      dotLottie?.play()
    }
  }, [autoplayType, isVisible])

  // Event handling
  useEffect(() => {
    if (!dotLottie) return
    function handleLoad() {
      setIsLoaded(true)
      onLoad?.(dotLottie)

      if (dotLottie) {
        // Match size of canvas to animation
        const animationSize = dotLottie.animationSize()
        if (dotLottie.canvas instanceof HTMLCanvasElement) {
          dotLottie.canvas.setAttribute('width', `${animationSize.width}`)
          dotLottie.canvas.setAttribute('height', `${animationSize.height}`)
          dotLottie.resize()
        }
      }
    }
    function handleComplete() {
      onComplete?.(dotLottie!)
    }
    function handleReady() {
      onReady?.(dotLottie!)
    }

    dotLottie.addEventListener('load', handleLoad)
    dotLottie.addEventListener('complete', handleComplete)
    dotLottie.addEventListener('ready', handleReady)
    return () => {
      dotLottie?.removeEventListener('load', handleLoad)
      dotLottie?.removeEventListener('complete', handleComplete)
      dotLottie?.removeEventListener('ready', handleReady)
    }
  }, [dotLottie])

  // Handle autoplay
  useEffect(() => {
    if (autoplay && isVisible && isLoaded) {
      play()
    }
  }, [isVisible, isLoaded])

  // Handle when src changes
  useEffect(() => {
    if (src && src !== prevSrc) {
      dotLottie?.stop()
      setIsLoaded(false)
    }
  }, [src])

  if (!src) return null
  return (
    <div
      ref={containerRef}
      onClick={autoplayType === 'click' ? play : undefined}
      onKeyUp={
        autoplayType === 'click'
          ? (e) => {
              if (e.key === 'Enter') play()
            }
          : undefined
      }
      className={cx(className, autoplayType === 'click' && 'cursor-pointer')}
      onMouseEnter={autoplayType === 'hover' ? () => dotLottie?.play() : undefined}
      onFocus={autoplayType === 'hover' ? () => dotLottie?.play() : undefined}
    >
      <DotLottieReact
        id={lottieId}
        dotLottieRefCallback={(instance) => {
          setDotLottie(instance)
          onInit?.(instance)
        }}
        src={src}
        autoplay={false}
        loop={loop}
        className="w-full h-full"
        marker={marker}
        renderConfig={{
          autoResize: false,
          freezeOnOffscreen: false,
        }}
      />
    </div>
  )
}
